import { useTranslation } from "react-i18next";
import { Select } from "../../components/Select";
import { useTheme } from "../../theme/ThemeProvider";
import { THEMES, THEME_ORDER, type ReducedMotion, type TextSize, type ThemeId } from "../../theme/themes";
import { ThemePreview } from "./ThemePreview";

const TEXT_SIZES: TextSize[] = ["small", "medium", "large"];
const MOTIONS: ReducedMotion[] = ["system", "on", "off"];

export function AppearanceCard() {
  const { t } = useTranslation();
  const { preferences, theme, setPreferences, reset } = useTheme();
  const light = THEME_ORDER.filter((id) => !THEMES[id].dark);
  const dark = THEME_ORDER.filter((id) => THEMES[id].dark);

  const choose = (id: ThemeId) =>
    void setPreferences({ theme: id, followSystem: false, ...(THEMES[id].onSelect ?? {}) });

  return (
    <section style={card}>
      <h2 style={{ margin: 0, fontSize: 16, color: "var(--brand-ink)" }}>{t("appearance.title")}</h2>
      <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>{t("appearance.hint")}</p>

      <div className="theme-grid" role="radiogroup" aria-label={t("appearance.themes")}>
        {THEME_ORDER.map((id) => (
          <ThemePreview
            key={id}
            theme={id}
            name={THEMES[id].name}
            description={t(`appearance.theme.${id}`)}
            selected={theme === id}
            onSelect={() => choose(id)}
          />
        ))}
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={preferences.followSystem}
          onChange={(e) => void setPreferences({ followSystem: e.target.checked })}
        />
        {t("appearance.followSystem")}
      </label>
      {preferences.followSystem && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 220px", minWidth: 0 }}>
            <span style={{ color: "var(--ink-2)", fontSize: 13 }}>{t("appearance.pair.light")}</span>
            <Select<ThemeId>
              ariaLabel={t("appearance.pair.light")}
              value={preferences.lightTheme}
              onChange={(v) => void setPreferences({ lightTheme: v })}
              options={light.map((id) => ({ value: id, label: THEMES[id].name }))}
              style={{ minWidth: 0 }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 220px", minWidth: 0 }}>
            <span style={{ color: "var(--ink-2)", fontSize: 13 }}>{t("appearance.pair.dark")}</span>
            <Select<ThemeId>
              ariaLabel={t("appearance.pair.dark")}
              value={preferences.darkTheme}
              onChange={(v) => void setPreferences({ darkTheme: v })}
              options={dark.map((id) => ({ value: id, label: THEMES[id].name }))}
              style={{ minWidth: 0 }}
            />
          </div>
        </div>
      )}

      <Setting label={t("appearance.textSize.label")}>
        <div className="seg">
          {TEXT_SIZES.map((s) => (
            <button key={s} type="button" aria-pressed={preferences.textSize === s} onClick={() => void setPreferences({ textSize: s })}
              style={{ fontSize: s === "small" ? 12 : s === "large" ? 16 : 14 }}>
              {t(`appearance.textSize.${s}`)}
            </button>
          ))}
        </div>
      </Setting>
      <Setting label={t("appearance.motion.label")}>
        <div className="seg">
          {MOTIONS.map((m) => (
            <button key={m} type="button" aria-pressed={preferences.reducedMotion === m} onClick={() => void setPreferences({ reducedMotion: m })}>
              {t(`appearance.motion.${m}`)}
            </button>
          ))}
        </div>
      </Setting>

      <div>
        <button type="button" className="btn" onClick={() => void reset()}>{t("appearance.reset")}</button>
      </div>
    </section>
  );
}

function Setting({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="appearance-setting">
      <span style={{ color: "var(--ink-2)", fontSize: 13 }}>{label}</span>
      {children}
    </div>
  );
}

// Matches the Profile page's own card style (frontend/src/pages/ProfilePage.tsx)
// rather than the shared .card class, so this card sits flush with the others.
const card: React.CSSProperties = {
  background: "var(--surface)",
  padding: 20,
  borderRadius: 10,
  boxShadow: "var(--shadow-1)",
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
